import { EVENT_TYPES, queueEvent, getGameState } from '../gameplay/battle-core.js';
import { SHIELD_COOLDOWN_MS, CHARGE_MAX_MS, CHARGE_MIN_MS } from '../gameplay/battle-constants.js';
import { isMobileChoiceActive } from './devicePicker.js';

// Mobile control scheme:
//   - 1-finger tap = quick attack, aimed toward the tap
//   - 1-finger hold-in-place = charge attack; release fires with chargeMs
//   - 1-finger horizontal drag = walk that way (no attack on release)
//   - 1-finger upward swipe = jump
//   - 2-finger tap = Q ability
//   - Shield button (bottom-right) = raise / drop the glass shield
// Desktop keeps its keyboard controls; the help overlay describes both.

const TAP_MAX_MS = 350;          // longer than this = ignored (not a tap)
const TAP_MAX_MOVE_PX = 30;       // moved further than this = ignored
// Horizontal drift past this on a 1-finger gesture turns it into a
// drag-to-move. Higher than TAP_MAX_MOVE_PX so a tap with a tiny wobble
// still fires an attack.
const MOVE_DRAG_THRESHOLD_PX = 40;
// Upward drift past this triggers a JUMP_REQUEST once per gesture.
const JUMP_DRAG_THRESHOLD_PX = 55;

function getLocalPlayerId() {
    const gs = getGameState();
    if (!gs || !gs.multiplayer) return 'player_1';
    return gs.multiplayer.isMultiplayer ? gs.multiplayer.myPeerId : 'player_1';
}

function sendInput(payload) {
    const gs = getGameState();
    if (!gs || !gs.characters) return;
    if (gs.battleStatus && gs.battleStatus.battleResult) return;
    if (!gs.characters[payload.playerId]) return;
    queueEvent({ type: EVENT_TYPES.PLAYER_INPUT_RECEIVED, payload });
}

// Flip the local player's charge-aura fields so the renderer paints the
// halo while the finger is held. Same fields the keyboard 1/2/3 path
// writes to so the visual is identical regardless of input source.
function markLocalCharging(attackIndex) {
    const gs = getGameState();
    if (!gs || !gs.battleStatus) return;
    const pid = getLocalPlayerId();
    const me = gs.characters && gs.characters[pid];
    if (!me) return;
    me.chargingAttackIndex = attackIndex;
    me.chargeStartedAt = Date.now();
}
function clearLocalCharging() {
    const gs = getGameState();
    if (!gs) return;
    const pid = getLocalPlayerId();
    const me = gs.characters && gs.characters[pid];
    if (!me) return;
    me.chargingAttackIndex = null;
    me.chargeStartedAt = 0;
}
function clampChargeMs(heldMs) {
    if (!Number.isFinite(heldMs) || heldMs < CHARGE_MIN_MS) return 0;
    return Math.min(CHARGE_MAX_MS, heldMs);
}

// Synchronous shield toggle. Mirrors the keyboard 'F' press path from
// battle-manager.js so the shield raises / drops the same way.
function shieldTap() {
    const gs = getGameState();
    if (!gs || !gs.battleStatus) return;
    const pid = getLocalPlayerId();
    const me = gs.characters && gs.characters[pid];
    if (!me || me.shieldShattered || gs.battleStatus.battleResult) return;
    const now = gs.battleStatus.timestamp;
    if (me.isBlocking) {
        me.isBlocking = false;
        me.shieldCooldownUntil = now + SHIELD_COOLDOWN_MS;
    } else if (now >= (me.shieldCooldownUntil || 0)) {
        me.isBlocking = true;
        me.shieldRaisedAt = now;
        me.shieldAngle = (me.direction === -1) ? Math.PI : 0;
    }
}

// Translate a screen-space tap into an aim angle from the local player's
// on-canvas center to the tap point. Falls back to null (= facing
// direction) if the canvas, character, or camera state isn't ready yet.
function aimAngleFromTap(screenX, screenY) {
    const canvas = document.getElementById('gameCanvas-manager');
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    if (!rect.width || !rect.height) return null;
    const gs = getGameState();
    const pid = getLocalPlayerId();
    const me = gs && gs.characters && gs.characters[pid];
    if (!me) return null;
    const camX = (gs.battleStatus && gs.battleStatus.cameraXOffset) || 0;
    const camY = (gs.battleStatus && gs.battleStatus.cameraYOffset) || 0;
    const zoom = Math.max(0.55, Math.min(1, (gs.battleStatus && gs.battleStatus.cameraZoom) || 1));
    const scaleX = rect.width / canvas.width;
    const scaleY = rect.height / canvas.height;
    const charScreenX = rect.left + (me.x + (me.width || 0) / 2 - camX) * zoom * scaleX;
    const charScreenY = rect.top  + (me.y + (me.height || 0) / 2 - camY) * zoom * scaleY;
    return Math.atan2(screenY - charScreenY, screenX - charScreenX);
}

function isTouchDevice() {
    try {
        if (typeof window === 'undefined') return false;
        return ('ontouchstart' in window) ||
            (navigator && navigator.maxTouchPoints > 0) ||
            window.matchMedia('(pointer: coarse)').matches;
    } catch (_) {
        return false;
    }
}

class MobileControlsManager {
    constructor() {
        this.isEnabled = false;
        this.controlsContainer = null;
        this.buttonBar = null;
        this.helpButton = null;
        this.shieldButton = null;
        this.tipOverlay = null;
        this.aimReticle = null;        // DOM element that tracks the finger
        this.onboardingHint = null;    // first-battle "tap ❓" callout
        this._onboardingTimer = null;
        // touchId → { startX, startY, currentX, currentY, startTime }
        this.touches = new Map();
        // Max simultaneous fingers seen during this gesture. Used at
        // touchend to disambiguate 1-finger tap (attack) vs 2-finger
        // gesture (ability), even if the second finger lands later.
        this.maxFingers = 0;
        // Movement state for the current 1-finger drag.
        this.currentMoveDir = 0;     // -1/0/+1 last sent via MOVE_INTENT
        this.hasDragged = false;     // true once the touch crossed MOVE_DRAG_THRESHOLD_PX
        this.hasJumpedThisGesture = false;
    }

    createHelpButton() {
        if (this.helpButton) return;
        const btn = document.createElement('button');
        btn.id = 'controls-help-button';
        btn.className = 'mobile-help-button';
        btn.innerHTML = '❓';
        btn.title = 'Controls';
        btn.setAttribute('aria-label', 'Controls help');
        btn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.dismissOnboardingHint();
            this.showHelp();
        });
        btn.addEventListener('touchstart', (e) => { e.stopPropagation(); }, { passive: true });
        document.body.appendChild(btn);
        this.helpButton = btn;
        // First-battle onboarding callout — a pulsing arrow + "New here?"
        // bubble that points at this button. Persists until the player
        // either taps the help button, dismisses the bubble, or 12s pass.
        this.maybeShowOnboardingHint();
    }

    maybeShowOnboardingHint() {
        try {
            if (localStorage.getItem('snordeluxe_onboarding_seen') === '1') return;
        } catch (_) {}
        if (this.onboardingHint) return;
        const hint = document.createElement('div');
        hint.id = 'controls-onboarding-hint';
        hint.className = 'controls-onboarding-hint';
        hint.innerHTML = `
            <div class="onboarding-bubble">
                <strong>New here?</strong>
                <span>Tap ❓ for controls.</span>
                <button type="button" class="onboarding-close" aria-label="Dismiss">×</button>
            </div>
            <div class="onboarding-arrow">▶</div>
        `;
        hint.querySelector('.onboarding-close').addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.dismissOnboardingHint();
        });
        document.body.appendChild(hint);
        this.onboardingHint = hint;
        // Auto-dismiss after 12s so it doesn't linger forever.
        this._onboardingTimer = setTimeout(() => this.dismissOnboardingHint(), 12000);
    }

    dismissOnboardingHint() {
        if (this._onboardingTimer) { clearTimeout(this._onboardingTimer); this._onboardingTimer = null; }
        if (!this.onboardingHint) return;
        try { localStorage.setItem('snordeluxe_onboarding_seen', '1'); } catch (_) {}
        const el = this.onboardingHint;
        this.onboardingHint = null;
        el.style.transition = 'opacity 0.25s ease';
        el.style.opacity = '0';
        setTimeout(() => { if (el.parentNode) el.parentNode.removeChild(el); }, 260);
    }

    removeHelpButton() {
        if (this.helpButton) {
            this.helpButton.remove();
            this.helpButton = null;
        }
    }

    showHelp() {
        if (this.tipOverlay) return;
        const overlay = document.createElement('div');
        overlay.className = 'mobile-tips-overlay controls-help-overlay';
        overlay.innerHTML = `
            <div class="tips-content controls-help-content">
                <h3>How to play</h3>
                <div class="controls-help-cols">
                    <div class="controls-help-col">
                        <h4>🖥️ Computer</h4>
                        <div class="tip-item"><span class="tip-icon">⬅️➡️</span><span class="tip-text"><b>A / D</b> — move left / right</span></div>
                        <div class="tip-item"><span class="tip-icon">⬆️</span><span class="tip-text"><b>W</b> or <b>Space</b> — jump</span></div>
                        <div class="tip-item"><span class="tip-icon">🎯</span><span class="tip-text"><b>1 / 2 / 3</b> — attacks (hold to charge)</span></div>
                        <div class="tip-item"><span class="tip-icon">✨</span><span class="tip-text"><b>Q</b> — special ability</span></div>
                        <div class="tip-item"><span class="tip-icon">🛡️</span><span class="tip-text"><b>F</b> — shield</span></div>
                        <div class="tip-item"><span class="tip-icon">🖱️</span><span class="tip-text"><b>Click + hold</b> — aim wheel; release fires a charged shot</span></div>
                    </div>
                    <div class="controls-help-col">
                        <h4>📱 Mobile</h4>
                        <div class="tip-item"><span class="tip-icon">👆</span><span class="tip-text"><b>Tap anywhere</b> — quick shot toward your finger</span></div>
                        <div class="tip-item"><span class="tip-icon">⏳</span><span class="tip-text"><b>Press &amp; hold</b> in place — charge a stronger shot</span></div>
                        <div class="tip-item"><span class="tip-icon">↔️</span><span class="tip-text"><b>Drag left / right</b> — walk that way</span></div>
                        <div class="tip-item"><span class="tip-icon">⬆️</span><span class="tip-text"><b>Swipe up</b> — jump</span></div>
                        <div class="tip-item"><span class="tip-icon">✌️</span><span class="tip-text"><b>Two-finger tap</b> — special ability</span></div>
                        <div class="tip-item"><span class="tip-icon">🛡</span><span class="tip-text"><b>Shield button</b> (bottom-left) — raise / drop</span></div>
                    </div>
                </div>
                <div class="tip-note">💡 Every hit has a <b>15% chance</b> to crit for <b>1.75×</b> damage — listen for the boom.</div>
                <button type="button" class="tips-dismiss controls-help-close">Got it</button>
            </div>
        `;
        const close = () => this.dismissHelp();
        overlay.querySelector('.controls-help-close').addEventListener('click', close);
        overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });
        document.body.appendChild(overlay);
        this.tipOverlay = overlay;
    }

    dismissHelp() {
        if (!this.tipOverlay) return;
        const el = this.tipOverlay;
        this.tipOverlay = null;
        el.style.transition = 'opacity 0.2s ease';
        el.style.opacity = '0';
        setTimeout(() => { if (el.parentNode) el.parentNode.removeChild(el); }, 220);
    }

    createMobileControls() {
        if (this.controlsContainer) return;
        const el = document.createElement('div');
        el.id = 'mobile-controls';
        el.className = 'mobile-touch-surface';
        el.addEventListener('touchstart', (e) => this.onTouchStart(e), { passive: false });
        el.addEventListener('touchmove',  (e) => this.onTouchMove(e),  { passive: false });
        el.addEventListener('touchend',   (e) => this.onTouchEnd(e),   { passive: false });
        el.addEventListener('touchcancel',(e) => this.onTouchEnd(e),   { passive: false });
        document.body.appendChild(el);
        this.controlsContainer = el;
        this.createButtonBar();
        this.createShieldButton();
        this.createAimReticle();
    }

    createButtonBar() {
        if (this.buttonBar) return;
        const bar = document.createElement('div');
        bar.id = 'mobile-button-bar';
        bar.innerHTML = `
            <div class="mbb-cluster">
                <button type="button" class="mbb-btn mbb-btn-move" data-dir="-1" aria-label="Move left">◀</button>
                <button type="button" class="mbb-btn mbb-btn-jump" aria-label="Jump">JUMP</button>
                <button type="button" class="mbb-btn mbb-btn-move" data-dir="1" aria-label="Move right">▶</button>
            </div>
            <div class="mbb-cluster">
                <button type="button" class="mbb-btn mbb-btn-shield" aria-label="Raise or drop shield">🛡</button>
                <button type="button" class="mbb-btn mbb-btn-ability" aria-label="Use special ability">Q</button>
                <button type="button" class="mbb-btn mbb-btn-atk" data-attack="0" aria-label="Attack 1">1</button>
                <button type="button" class="mbb-btn mbb-btn-atk" data-attack="1" aria-label="Attack 2">2</button>
                <button type="button" class="mbb-btn mbb-btn-atk" data-attack="2" aria-label="Attack 3">3</button>
            </div>
        `;

        const stopEvent = (e) => {
            e.preventDefault();
            e.stopPropagation();
        };
        const flash = (btn) => {
            btn.classList.add('is-pressed');
            setTimeout(() => btn.classList.remove('is-pressed'), 120);
        };

        bar.querySelectorAll('.mbb-btn-move').forEach((btn) => {
            const dir = Number(btn.getAttribute('data-dir')) || 0;
            const start = (e) => {
                stopEvent(e);
                flash(btn);
                sendInput({ playerId: getLocalPlayerId(), inputType: 'MOVE_INTENT', direction: dir });
            };
            const end = (e) => {
                stopEvent(e);
                sendInput({ playerId: getLocalPlayerId(), inputType: 'MOVE_INTENT', direction: 0 });
            };
            btn.addEventListener('touchstart', start, { passive: false });
            btn.addEventListener('touchend', end, { passive: false });
            btn.addEventListener('touchcancel', end, { passive: false });
            btn.addEventListener('mousedown', start);
            btn.addEventListener('mouseup', end);
            btn.addEventListener('mouseleave', end);
        });

        const jumpBtn = bar.querySelector('.mbb-btn-jump');
        jumpBtn.addEventListener('click', (e) => {
            stopEvent(e);
            flash(jumpBtn);
            sendInput({ playerId: getLocalPlayerId(), inputType: 'JUMP_REQUEST' });
        });

        const shieldBtn = bar.querySelector('.mbb-btn-shield');
        shieldBtn.addEventListener('click', (e) => {
            stopEvent(e);
            flash(shieldBtn);
            shieldTap();
        });

        const abilityBtn = bar.querySelector('.mbb-btn-ability');
        abilityBtn.addEventListener('click', (e) => {
            stopEvent(e);
            flash(abilityBtn);
            this.fireAbility();
        });

        bar.querySelectorAll('.mbb-btn-atk').forEach((btn) => {
            btn.addEventListener('click', (e) => {
                stopEvent(e);
                flash(btn);
                const attackIndex = Number(btn.getAttribute('data-attack')) || 0;
                const gs = getGameState();
                const pid = getLocalPlayerId();
                const me = gs && gs.characters && gs.characters[pid];
                if (!me || me.isBlocking) return;
                sendInput({
                    playerId: pid,
                    inputType: 'ATTACK_INTENT',
                    attackIndex,
                    aimAngle: null,
                    chargeMs: 0,
                });
            });
        });

        bar.addEventListener('touchstart', stopEvent, { passive: false });
        bar.addEventListener('contextmenu', (e) => e.preventDefault());
        document.body.appendChild(bar);
        this.buttonBar = bar;
    }

    createAimReticle() {
        if (this.aimReticle) return;
        const r = document.createElement('div');
        r.id = 'mobile-aim-reticle';
        r.className = 'mobile-aim-reticle';
        r.style.display = 'none';
        document.body.appendChild(r);
        this.aimReticle = r;
    }

    showAimReticle(x, y) {
        if (!this.aimReticle) return;
        this.aimReticle.style.left = `${x}px`;
        this.aimReticle.style.top = `${y}px`;
        this.aimReticle.style.display = 'block';
    }

    hideAimReticle() {
        if (this.aimReticle) this.aimReticle.style.display = 'none';
    }

    createShieldButton() {
        if (this.shieldButton) return;
        const btn = document.createElement('button');
        btn.id = 'mobile-shield-button';
        btn.className = 'mobile-shield-button';
        btn.innerHTML = '🛡';
        btn.title = 'Shield';
        btn.setAttribute('aria-label', 'Raise / drop shield');
        // Tap = toggle shield. Stop propagation so the touch surface
        // doesn't also interpret it as a tap-to-shoot.
        const fire = (e) => {
            e.preventDefault();
            e.stopPropagation();
            shieldTap();
            btn.classList.add('is-pressed');
            setTimeout(() => btn.classList.remove('is-pressed'), 120);
        };
        btn.addEventListener('touchstart', fire, { passive: false });
        btn.addEventListener('mousedown', fire);
        btn.addEventListener('contextmenu', (e) => e.preventDefault());
        document.body.appendChild(btn);
        this.shieldButton = btn;
    }

    removeShieldButton() {
        if (this.shieldButton) {
            this.shieldButton.remove();
            this.shieldButton = null;
        }
    }

    isOverUI(x, y) {
        const el = document.elementFromPoint(x, y);
        if (!el) return false;
        // Anything inside an open modal, the help overlay, or battle
        // results should receive its own clicks — let them through.
        if (el.closest('.modal-overlay, .battle-results-overlay, .mobile-tips-overlay, #devicePickerModal')) return true;
        const criticalIds = [
            'leave-battle-button', 'leave-game-button',
            'multiplayer_cancel_join_button', 'controls-help-button',
            'mobile-shield-button', 'mobile-button-bar'
        ];
        if (criticalIds.includes(el.id)) return true;
        if (el.closest('#mobile-button-bar')) return true;
        // Generic buttons (HUD doesn't render any over the canvas, so this
        // is just a safety net for future overlay buttons).
        if (el.tagName === 'BUTTON') return true;
        return false;
    }

    onTouchStart(event) {
        const touches = event.changedTouches || [];
        for (const t of touches) {
            if (this.isOverUI(t.clientX, t.clientY)) return;
        }
        event.preventDefault();
        for (const t of touches) {
            this.touches.set(t.identifier, {
                startX: t.clientX, startY: t.clientY,
                currentX: t.clientX, currentY: t.clientY,
                startTime: Date.now(),
            });
        }
        if (this.touches.size > this.maxFingers) {
            this.maxFingers = this.touches.size;
        }
        // First finger landing on a fresh 1-finger gesture → start the
        // charge aura immediately. If the gesture turns into a drag
        // (movement) or a second finger lands (ability), we cancel.
        if (this.touches.size === 1 && this.maxFingers === 1) {
            markLocalCharging(0);
            const first = touches[0];
            if (first) this.showAimReticle(first.clientX, first.clientY);
        } else if (this.maxFingers >= 2) {
            clearLocalCharging();
            this.hideAimReticle();
        }
    }

    onTouchMove(event) {
        event.preventDefault();
        for (const t of event.changedTouches || []) {
            const info = this.touches.get(t.identifier);
            if (info) {
                info.currentX = t.clientX;
                info.currentY = t.clientY;
            }
        }
        // Drag-to-move: only the first finger drives movement. If the
        // gesture is 2+ fingers (it's heading toward the ability path),
        // skip — we don't want a Q-tap to also shove the character.
        if (this.maxFingers >= 2) return;
        const first = this.touches.values().next().value;
        if (!first) return;
        const dx = first.currentX - first.startX;
        const dy = first.currentY - first.startY;
        if (Math.abs(dx) >= MOVE_DRAG_THRESHOLD_PX || Math.abs(dy) >= MOVE_DRAG_THRESHOLD_PX) {
            this.hasDragged = true;
        }
        if (this.hasDragged) {
            // A drag overrides charging — the gesture is now movement.
            clearLocalCharging();
            this.hideAimReticle();
            const desired = (Math.abs(dx) < MOVE_DRAG_THRESHOLD_PX * 0.4)
                ? 0
                : (dx > 0 ? 1 : -1);
            if (desired !== this.currentMoveDir) {
                this.currentMoveDir = desired;
                sendInput({ playerId: getLocalPlayerId(), inputType: 'MOVE_INTENT', direction: desired });
            }
            // Upward swipe past the jump threshold fires JUMP_REQUEST once
            // per gesture. Subsequent drags don't re-trigger.
            if (!this.hasJumpedThisGesture && dy <= -JUMP_DRAG_THRESHOLD_PX) {
                this.hasJumpedThisGesture = true;
                sendInput({ playerId: getLocalPlayerId(), inputType: 'JUMP_REQUEST' });
            }
        } else if (this.maxFingers === 1) {
            // Still aiming — keep the reticle on the finger.
            this.showAimReticle(first.currentX, first.currentY);
        }
    }

    stopMovement() {
        if (this.currentMoveDir === 0) return;
        this.currentMoveDir = 0;
        sendInput({ playerId: getLocalPlayerId(), inputType: 'MOVE_INTENT', direction: 0 });
    }

    onTouchEnd(event) {
        event.preventDefault();
        const endings = [];
        for (const t of event.changedTouches || []) {
            const info = this.touches.get(t.identifier);
            if (info) endings.push(info);
            this.touches.delete(t.identifier);
        }
        // Resolve the gesture only after the LAST finger lifts. That's
        // how we tell a 1-finger tap (attack) from a 2+finger gesture
        // (ability) even when the second finger lands a few ms after
        // the first.
        if (this.touches.size > 0) return;

        const maxFingers = this.maxFingers;
        const wasDrag = this.hasDragged;
        this.maxFingers = 0;
        this.hasDragged = false;
        this.hasJumpedThisGesture = false;
        // Always stop movement when the last finger leaves — otherwise
        // the character would keep drifting after release.
        this.stopMovement();
        clearLocalCharging();
        this.hideAimReticle();

        if (maxFingers >= 2) {
            this.fireAbility();
            return;
        }

        // If this was a drag (movement), don't also fire an attack —
        // the drag was for movement intent, not aim.
        if (wasDrag) return;

        const primary = endings[0];
        if (!primary) return;
        const dx = primary.currentX - primary.startX;
        const dy = primary.currentY - primary.startY;
        const dist = Math.hypot(dx, dy);
        const heldMs = Date.now() - primary.startTime;
        // Drift past the tap drift threshold → not a real tap, ignore.
        if (dist > TAP_MAX_MOVE_PX) return;
        // chargeMs: 0 for quick taps, clamped to [CHARGE_MIN_MS, CHARGE_MAX_MS]
        // for sustained presses. Matches the keyboard/desktop pipeline so
        // mobile players can charge attacks too.
        const chargeMs = clampChargeMs(heldMs);
        const aimAngle = aimAngleFromTap(primary.currentX, primary.currentY);
        this.fireAttack(aimAngle, chargeMs);
    }

    fireAttack(aimAngle, chargeMs = 0) {
        const gs = getGameState();
        const pid = getLocalPlayerId();
        const me = gs && gs.characters && gs.characters[pid];
        if (!me || me.isBlocking) return;
        sendInput({
            playerId: pid,
            inputType: 'ATTACK_INTENT',
            attackIndex: 0,
            aimAngle: (typeof aimAngle === 'number' && Number.isFinite(aimAngle)) ? aimAngle : null,
            chargeMs: chargeMs || 0,
        });
    }

    fireAbility() {
        const gs = getGameState();
        const pid = getLocalPlayerId();
        if (!gs || !gs.characters || !gs.characters[pid]) return;
        if (gs.battleStatus && gs.battleStatus.battleResult) return;
        queueEvent({ type: EVENT_TYPES.ABILITY_USE_REQUESTED, payload: { casterId: pid } });
    }

    isInBattle() {
        return !!document.getElementById('battleground-overlay-manager') ||
               !!document.getElementById('gameCanvas-manager');
    }

    enable() {
        if (!this.isInBattle()) return;
        if (!this.controlsContainer) this.createMobileControls();
        this.controlsContainer.style.display = 'block';
        if (this.buttonBar) this.buttonBar.classList.add('is-visible');
        if (this.shieldButton) this.shieldButton.style.display = 'flex';
        this.isEnabled = true;
        // Lock the page from scrolling/zooming while the touch surface
        // is active so a stray drag doesn't bounce the viewport around.
        document.body.style.overflow = 'hidden';
        document.body.style.position = 'fixed';
        document.body.style.width = '100%';
        document.body.style.height = '100%';
    }

    disable() {
        if (this.controlsContainer) this.controlsContainer.style.display = 'none';
        if (this.buttonBar) this.buttonBar.classList.remove('is-visible');
        if (this.shieldButton) this.shieldButton.style.display = 'none';
        this.isEnabled = false;
        if (!this.isInBattle()) {
            document.body.style.overflow = '';
            document.body.style.position = '';
            document.body.style.width = '';
            document.body.style.height = '';
        }
        this.touches.clear();
        this.maxFingers = 0;
    }

    destroy() {
        this.disable();
        if (this.controlsContainer) {
            this.controlsContainer.remove();
            this.controlsContainer = null;
        }
        if (this.buttonBar) {
            this.buttonBar.remove();
            this.buttonBar = null;
        }
        this.removeShieldButton();
        if (this.aimReticle) {
            this.aimReticle.remove();
            this.aimReticle = null;
        }
        if (this.tipOverlay) {
            this.tipOverlay.remove();
            this.tipOverlay = null;
        }
    }

    forceEnable() { this.enable(); }

    updateControlStates() {
        if (this.isInBattle() && !this.isEnabled) this.enable();
        else if (!this.isInBattle() && this.isEnabled) this.disable();
    }
}

const mobileControlsManager = new MobileControlsManager();

export function enableMobileControls() {
    if (!isTouchDevice() && !isMobileChoiceActive()) return;
    mobileControlsManager.enable();
}

export function disableMobileControls() {
    mobileControlsManager.disable();
}

export function destroyMobileControls() {
    mobileControlsManager.destroy();
}

export function updateMobileControlStates() {
    mobileControlsManager.updateControlStates();
}

export function forceMobileControls() {
    mobileControlsManager.forceEnable();
}

// The help button is created on game load AND on battle entry. It shows
// instructions for BOTH desktop and mobile in a single overlay, so PC
// players get the same affordance.
export function createMobileControlsToggle() {
    mobileControlsManager.createHelpButton();
}

export function refreshMobileControlsToggle() {}

export function initMobileControlsToggle() {
    mobileControlsManager.createHelpButton();
}

export default mobileControlsManager;
